import React from 'react';
import {
  Button,
  Card,
  Collapse,
  Drawer,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { runtimeQueryKeys } from '../../services/runtimeQueryKeys';
import { runtimeHistoryService } from '../../services/runtimeOperationalService';
import useRuntimeQuery from '../../hooks/useRuntimeQuery';
import { usePlatformActionPermissions } from '../../hooks/usePlatformActionPermissions';
import {
  RuntimeCorrelationChip,
  RuntimeEmptyState,
  RuntimeErrorState,
  RuntimeLoadingState,
} from '../common/runtime';
import RuntimeExecutionDetailPanel from './runtime/RuntimeExecutionDetailPanel';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const STATUS_COLOR = {
  success: 'green',
  completed: 'green',
  running: 'processing',
  pending: 'gold',
  failed: 'red',
  error: 'red',
  cancelled: 'default',
};

const RISK_COLOR = {
  low: 'green',
  medium: 'gold',
  high: 'red',
};

const PERIOD_OPTIONS = [
  { label: '24h', value: '24h' },
  { label: '7 dias', value: '7d' },
  { label: '30 dias', value: '30d' },
  { label: 'Tudo', value: 'all' },
];

const toLower = (value) => String(value || '').trim().toLowerCase();

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString('pt-BR');
};

const formatDuration = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  return `${numeric} ms`;
};

const getStatusTag = (value) => {
  const normalized = toLower(value);
  const color = STATUS_COLOR[normalized] || 'default';
  return <Tag color={color}>{String(value || '-').toUpperCase()}</Tag>;
};

const getRiskLevel = (row) => {
  const explicitRisk = toLower(row?.risk_level);
  if (explicitRisk) {
    return explicitRisk;
  }

  const status = toLower(row?.status);
  if (status === 'failed' || status === 'error') {
    return 'high';
  }
  if (status === 'running' || status === 'pending') {
    return 'medium';
  }
  return 'low';
};

const getRiskTag = (row) => {
  const risk = getRiskLevel(row);
  return <Tag color={RISK_COLOR[risk] || 'default'}>{risk.toUpperCase()}</Tag>;
};

const getLastEventAt = (row) =>
  row?.last_event_at || row?.finished_at || row?.started_at || row?.updated_at || row?.created_at || null;

const normalizeItems = (responseData) =>
  Array.isArray(responseData?.items) ? responseData.items : [];

const mergeByExecutionId = (historyItems, searchItems) => {
  const map = new Map();

  [...historyItems, ...searchItems].forEach((item) => {
    const id = String(item?.execution_id || '').trim();
    if (!id) {
      return;
    }

    const existing = map.get(id) || {};
    map.set(id, {
      ...existing,
      ...item,
      execution_id: id,
    });
  });

  return Array.from(map.values());
};

const periodInMs = (period) => {
  if (period === '24h') return 24 * 60 * 60 * 1000;
  if (period === '7d') return 7 * 24 * 60 * 60 * 1000;
  if (period === '30d') return 30 * 24 * 60 * 60 * 1000;
  return null;
};

const collectKeyValues = (value, keyName, output) => {
  if (Array.isArray(value)) {
    value.forEach((entry) => collectKeyValues(entry, keyName, output));
    return;
  }

  if (!value || typeof value !== 'object') {
    return;
  }

  Object.entries(value).forEach(([key, nestedValue]) => {
    if (toLower(key) === toLower(keyName)) {
      output.add(toLower(nestedValue));
    }
    collectKeyValues(nestedValue, keyName, output);
  });
};

const matchesAdvancedField = (row, fieldKey, expectedValue) => {
  const expected = toLower(expectedValue);
  if (!expected) return true;

  const directValue = toLower(row?.[fieldKey]);
  if (directValue && directValue.includes(expected)) {
    return true;
  }

  const discovered = new Set();
  collectKeyValues(row?.summary_json, fieldKey, discovered);
  collectKeyValues(row?.payload_sanitized, fieldKey, discovered);
  collectKeyValues(row?.metadata_json, fieldKey, discovered);

  return Array.from(discovered).some((value) => value.includes(expected));
};

const PlatformRuntimeIncidentsPage = () => {
  const navigate = useNavigate();
  const { canExecuteAction } = usePlatformActionPermissions();

  const [quickFilters, setQuickFilters] = React.useState({
    tenant_id: '',
    period: '7d',
    status: undefined,
    operation_type: undefined,
    source: undefined,
    correlation_id: '',
  });

  const [advancedFilters, setAdvancedFilters] = React.useState({
    request_id: '',
    invoice_id: '',
    charge_id: '',
    payment_intent_id: '',
    execution_id: '',
  });

  const [appliedQuickFilters, setAppliedQuickFilters] = React.useState({
    tenant_id: '',
    period: '7d',
    status: undefined,
    operation_type: undefined,
    source: undefined,
    correlation_id: '',
  });

  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = React.useState({
    request_id: '',
    invoice_id: '',
    charge_id: '',
    payment_intent_id: '',
    execution_id: '',
  });

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [drawerExecutionId, setDrawerExecutionId] = React.useState('');

  const historyParams = React.useMemo(
    () => ({
      tenant_id: String(appliedQuickFilters.tenant_id || '').trim() || undefined,
      status: appliedQuickFilters.status || undefined,
      operation_type: appliedQuickFilters.operation_type || undefined,
      limit: 200,
      offset: 0,
    }),
    [appliedQuickFilters]
  );

  const correlationForSearch = String(appliedQuickFilters.correlation_id || '').trim();

  const historyQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.executionsList(historyParams),
    queryFn: async () => {
      const response = await runtimeHistoryService.listExecutions(historyParams);
      return response.data;
    },
  });

  const searchQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.executionsSearch({ correlation_id: correlationForSearch, limit: 200, offset: 0 }),
    enabled: Boolean(correlationForSearch),
    queryFn: async () => {
      const response = await runtimeHistoryService.searchExecutionsByCorrelationId({
        correlation_id: correlationForSearch,
        limit: 200,
        offset: 0,
      });
      return response.data;
    },
  });

  const historyItems = normalizeItems(historyQuery.data);
  const searchItems = normalizeItems(searchQuery.data);

  const mergedItems = React.useMemo(
    () => mergeByExecutionId(historyItems, searchItems),
    [historyItems, searchItems]
  );

  const statusOptions = React.useMemo(() => {
    const values = [...new Set(mergedItems.map((item) => String(item?.status || '').trim()).filter(Boolean))].sort();
    return values.map((value) => ({ label: value.toUpperCase(), value }));
  }, [mergedItems]);

  const operationTypeOptions = React.useMemo(() => {
    const values = [...new Set(mergedItems.map((item) => String(item?.operation_type || '').trim()).filter(Boolean))].sort();
    return values.map((value) => ({ label: value, value }));
  }, [mergedItems]);

  const sourceOptions = React.useMemo(() => {
    const values = [...new Set(mergedItems.map((item) => String(item?.source || '').trim()).filter(Boolean))].sort();
    return values.map((value) => ({ label: value, value }));
  }, [mergedItems]);

  const filteredItems = React.useMemo(() => {
    const now = Date.now();
    const maxAgeMs = periodInMs(appliedQuickFilters.period);

    return mergedItems
      .filter((item) => {
        const tenant = toLower(item?.tenant_id);
        const status = toLower(item?.status);
        const operationType = toLower(item?.operation_type);
        const source = toLower(item?.source);
        const correlation = toLower(item?.correlation_id);

        const byTenant = !appliedQuickFilters.tenant_id || tenant.includes(toLower(appliedQuickFilters.tenant_id));
        const byStatus = !appliedQuickFilters.status || status === toLower(appliedQuickFilters.status);
        const byOperation =
          !appliedQuickFilters.operation_type ||
          operationType === toLower(appliedQuickFilters.operation_type);
        const bySource = !appliedQuickFilters.source || source === toLower(appliedQuickFilters.source);
        const byCorrelation =
          !appliedQuickFilters.correlation_id ||
          correlation.includes(toLower(appliedQuickFilters.correlation_id));

        let byPeriod = true;
        if (maxAgeMs !== null) {
          const lastEventAt = getLastEventAt(item);
          const parsed = lastEventAt ? new Date(lastEventAt).getTime() : NaN;
          byPeriod = Number.isFinite(parsed) ? now - parsed <= maxAgeMs : false;
        }

        return byTenant && byStatus && byOperation && bySource && byCorrelation && byPeriod;
      })
      .filter((item) => matchesAdvancedField(item, 'request_id', appliedAdvancedFilters.request_id))
      .filter((item) => matchesAdvancedField(item, 'invoice_id', appliedAdvancedFilters.invoice_id))
      .filter((item) => matchesAdvancedField(item, 'charge_id', appliedAdvancedFilters.charge_id))
      .filter((item) => matchesAdvancedField(item, 'payment_intent_id', appliedAdvancedFilters.payment_intent_id))
      .filter((item) => matchesAdvancedField(item, 'execution_id', appliedAdvancedFilters.execution_id))
      .sort((left, right) => {
        const leftDate = new Date(getLastEventAt(left) || 0).getTime();
        const rightDate = new Date(getLastEventAt(right) || 0).getTime();
        return rightDate - leftDate;
      });
  }, [appliedAdvancedFilters, appliedQuickFilters, mergedItems]);

  const pagedItems = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  const isLoading = historyQuery.isLoading || searchQuery.isLoading;
  const isFetching = historyQuery.isFetching || searchQuery.isFetching;
  const isError = historyQuery.isError || searchQuery.isError;
  const combinedError = historyQuery.error || searchQuery.error;

  const apiError = isError
    ? buildApiErrorMessage(combinedError, 'Nao foi possivel carregar Runtime Incidents.')
    : null;

  const canReadRetryPreview = canExecuteAction('runtime.preview.read');

  const applyFilters = () => {
    setAppliedQuickFilters({ ...quickFilters });
    setAppliedAdvancedFilters({ ...advancedFilters });
    setPage(1);
  };

  const clearFilters = () => {
    const initialQuick = {
      tenant_id: '',
      period: '7d',
      status: undefined,
      operation_type: undefined,
      source: undefined,
      correlation_id: '',
    };

    const initialAdvanced = {
      request_id: '',
      invoice_id: '',
      charge_id: '',
      payment_intent_id: '',
      execution_id: '',
    };

    setQuickFilters(initialQuick);
    setAdvancedFilters(initialAdvanced);
    setAppliedQuickFilters(initialQuick);
    setAppliedAdvancedFilters(initialAdvanced);
    setPage(1);
  };

  const handleRefresh = () => {
    historyQuery.refetch();
    if (correlationForSearch) {
      searchQuery.refetch();
    }
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => getStatusTag(value),
    },
    {
      title: 'Operation',
      dataIndex: 'operation_type',
      key: 'operation_type',
      render: (value) => value || '-',
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      render: (value) => value || '-',
    },
    {
      title: 'Correlation ID',
      dataIndex: 'correlation_id',
      key: 'correlation_id',
      render: (value) => <RuntimeCorrelationChip correlationId={value} />,
    },
    {
      title: 'Ultimo Evento',
      key: 'last_event',
      render: (_, record) => formatDateTime(getLastEventAt(record)),
    },
    {
      title: 'Duracao',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      render: (value) => formatDuration(value),
    },
    {
      title: 'Origem',
      dataIndex: 'source',
      key: 'source',
      render: (value) => value || '-',
    },
    {
      title: 'Risco',
      key: 'risk',
      render: (_, record) => getRiskTag(record),
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, record) => {
        const executionId = String(record?.execution_id || '').trim();
        return (
          <Space>
            <Button
              type="link"
              size="small"
              disabled={!executionId}
              onClick={() => setDrawerExecutionId(executionId)}
            >
              View Execution
            </Button>
            <Button
              type="link"
              size="small"
              disabled={!executionId || !canReadRetryPreview}
              onClick={() =>
                navigate(
                  `/platform/saas-billing/runtime/executions/${encodeURIComponent(
                    executionId
                  )}/retry-preview`
                )
              }
            >
              Retry Analysis
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }} wrap>
            <div>
              <Title level={2} style={{ marginTop: 0, marginBottom: 0 }}>
                Runtime Incidents
              </Title>
              <Text type="secondary">
                Triagem operacional unificada de incidentes e execucoes.
              </Text>
            </div>

            <Space>
              <Button onClick={handleRefresh} loading={isFetching}>
                Atualizar
              </Button>
              <Button onClick={clearFilters}>Limpar filtros</Button>
            </Space>
          </Space>
        </div>

        <Card>
          <Space wrap size={12}>
            <Input
              placeholder="tenant"
              value={quickFilters.tenant_id}
              onChange={(event) =>
                setQuickFilters((prev) => ({ ...prev, tenant_id: event.target.value }))
              }
              style={{ minWidth: 200 }}
            />

            <Select
              placeholder="periodo"
              value={quickFilters.period}
              options={PERIOD_OPTIONS}
              onChange={(value) => setQuickFilters((prev) => ({ ...prev, period: value }))}
              style={{ minWidth: 130 }}
            />

            <Select
              placeholder="status"
              value={quickFilters.status}
              options={statusOptions}
              allowClear
              onChange={(value) => setQuickFilters((prev) => ({ ...prev, status: value }))}
              style={{ minWidth: 160 }}
            />

            <Select
              placeholder="operation type"
              value={quickFilters.operation_type}
              options={operationTypeOptions}
              allowClear
              onChange={(value) => setQuickFilters((prev) => ({ ...prev, operation_type: value }))}
              style={{ minWidth: 220 }}
            />

            <Select
              placeholder="origem"
              value={quickFilters.source}
              options={sourceOptions}
              allowClear
              onChange={(value) => setQuickFilters((prev) => ({ ...prev, source: value }))}
              style={{ minWidth: 180 }}
            />

            <Input
              placeholder="correlation id"
              value={quickFilters.correlation_id}
              onChange={(event) =>
                setQuickFilters((prev) => ({ ...prev, correlation_id: event.target.value }))
              }
              style={{ minWidth: 260 }}
            />

            <Button type="primary" onClick={applyFilters}>
              Aplicar filtros
            </Button>
          </Space>

          <Collapse ghost style={{ marginTop: 12 }}>
            <Panel header="Filtros avancados" key="advanced-filters">
              <Space wrap size={12}>
                <Input
                  placeholder="request id"
                  value={advancedFilters.request_id}
                  onChange={(event) =>
                    setAdvancedFilters((prev) => ({ ...prev, request_id: event.target.value }))
                  }
                  style={{ minWidth: 220 }}
                />

                <Input
                  placeholder="invoice id"
                  value={advancedFilters.invoice_id}
                  onChange={(event) =>
                    setAdvancedFilters((prev) => ({ ...prev, invoice_id: event.target.value }))
                  }
                  style={{ minWidth: 220 }}
                />

                <Input
                  placeholder="charge id"
                  value={advancedFilters.charge_id}
                  onChange={(event) =>
                    setAdvancedFilters((prev) => ({ ...prev, charge_id: event.target.value }))
                  }
                  style={{ minWidth: 220 }}
                />

                <Input
                  placeholder="payment intent id"
                  value={advancedFilters.payment_intent_id}
                  onChange={(event) =>
                    setAdvancedFilters((prev) => ({ ...prev, payment_intent_id: event.target.value }))
                  }
                  style={{ minWidth: 220 }}
                />

                <Input
                  placeholder="execution id"
                  value={advancedFilters.execution_id}
                  onChange={(event) =>
                    setAdvancedFilters((prev) => ({ ...prev, execution_id: event.target.value }))
                  }
                  style={{ minWidth: 220 }}
                />
              </Space>
            </Panel>
          </Collapse>
        </Card>

        <Card>
          {isLoading ? <RuntimeLoadingState /> : null}

          {!isLoading && isError ? (
            <RuntimeErrorState
              title="Erro ao carregar Runtime Incidents"
              description={apiError?.message || 'Falha inesperada ao carregar incidentes.'}
              onRetry={handleRefresh}
            />
          ) : null}

          {!isLoading && !isError && filteredItems.length === 0 ? (
            <RuntimeEmptyState
              title="Nenhum incidente encontrado"
              description="Ajuste os filtros para localizar execucoes e incidentes operacionais."
            />
          ) : null}

          {!isLoading && !isError && filteredItems.length > 0 ? (
            <Table
              rowKey="execution_id"
              columns={columns}
              dataSource={pagedItems}
              pagination={{
                current: page,
                pageSize,
                total: filteredItems.length,
                showSizeChanger: true,
                onChange: (nextPage, nextPageSize) => {
                  setPage(nextPage);
                  if (nextPageSize !== pageSize) {
                    setPageSize(nextPageSize);
                  }
                },
              }}
            />
          ) : null}
        </Card>
      </Space>

      <Drawer
        title={drawerExecutionId ? `Execution ${drawerExecutionId}` : 'Execution'}
        open={Boolean(drawerExecutionId)}
        onClose={() => setDrawerExecutionId('')}
        width={760}
        destroyOnClose
      >
        {drawerExecutionId ? <RuntimeExecutionDetailPanel executionId={drawerExecutionId} /> : null}
      </Drawer>
    </div>
  );
};

export default PlatformRuntimeIncidentsPage;